import type { ReactNode } from 'react';
import { Anchor, Group, Image, Stack, Text } from '@mantine/core';
import type { SubmissionSupportConfig } from '../types/schema';

interface ProgramFooterProps {
  support?: SubmissionSupportConfig;
  tagline?: string;
  legalText?: string;
  prescribingInformationUrl?: string;
  feedbackUrl?: string;
  visible?: boolean;
  showLogo?: boolean;
  logoUrl?: string;
  children?: ReactNode;
}

export default function ProgramFooter({ support, tagline, legalText, prescribingInformationUrl, feedbackUrl, visible = false, showLogo = true, logoUrl = '/RIS_Rx_logo_digital_rgb_compact.png', children }: ProgramFooterProps) {
  if (!visible) {
    return null;
  }

  return (
    <footer className="portal-footer">
      <div className={`portal-shell-inner portal-footer__inner${children ? ' portal-footer__inner--with-action' : ''}`}>
        {children ? (
          <div className="portal-footer__legal-action-row">
            <Text size="xs" c="dimmed" ta="center" className="portal-footer__legal-text">
              {legalText}
              {import.meta.env.VITE_APP_VERSION && <> | v{import.meta.env.VITE_APP_VERSION}</>}
            </Text>
            <div className="portal-footer__action">{children}</div>
          </div>
        ) : null}
        {support && (support.name || support.phone || support.email) && (
          <Stack gap={2} className="portal-footer__support">
            <Text size="sm" c="dimmed">
              {support.intro ?? 'For assistance, contact:'}
            </Text>
            <Group gap="md" wrap="wrap">
              {support.name && <Text size="sm" c="dimmed" fw={600}>{support.name}</Text>}
              {support.phone && <Text size="sm" c="dimmed">Phone: {support.phone}</Text>}
              {support.email && <Text size="sm" c="dimmed">Email: {support.email}</Text>}
            </Group>
          </Stack>
        )}

        {(showLogo || feedbackUrl) && <Group justify={showLogo ? 'space-between' : 'center'} align="center" className="portal-powered-by" wrap="wrap">
          {showLogo && (
            <Group gap="sm" align="center" className="portal-powered-by__brand" wrap="nowrap" style={feedbackUrl ? { flex: 1 } : undefined}>
              <Text size="xs" fw={700} tt="uppercase" c="dimmed">Powered by</Text>
              <Image src={logoUrl} alt="RIS Rx" maw={108} fit="contain" className="portal-footer__logo" />
            </Group>
          )}
          {feedbackUrl && (
            <Text size="sm" c="dimmed" ta="center">
              Comments or feedback? Share your thoughts{' '}
              <Anchor href={feedbackUrl} target="_blank" rel="noopener noreferrer" size="sm">here</Anchor>.
            </Text>
          )}
          {showLogo && <Text size="xs" c="dimmed" ta="right" className="portal-footer__tagline" fs="italic">{tagline ?? '\u00a0'}</Text>}
        </Group>}
        
        {!children && (
          <Text size="xs" c="dimmed" ta="center" mt="md">
            {legalText ?? '© 2026 RIS Rx. All rights reserved. Confidential and proprietary. Unauthorized access, copying, disclosure, or use of EscalationIQ is prohibited.'}
            {import.meta.env.VITE_APP_VERSION && <> | v{import.meta.env.VITE_APP_VERSION}</>}
          </Text>
        )}
        {prescribingInformationUrl && (
          <Text size="xs" c="dimmed" ta="center">
            For more information, see accompanying full{' '}
            <Anchor href={prescribingInformationUrl} target="_blank" rel="noopener noreferrer" size="xs">Prescribing Information</Anchor>, including Boxed WARNING.
          </Text>
        )}
      </div>
    </footer>
  );
}
